"use client";

import { Button } from "@workspace/ui/components/button";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import Link from "next/link";
import { useState, useTransition } from "react";
import type { StoryblokStoryData } from "@/types/storyblok";
import { fetchPaginatedPages } from "./actions";

export interface ProcessedLink {
  url: string;
  title: string;
  key: string;
}

interface PaginatedLinksProps {
  // For manual links: all links provided upfront
  links?: ProcessedLink[];
  // For relational links: initial links + server-side pagination
  initialLinks?: ProcessedLink[];
  total?: number;
  route?: string;
  itemsPerPage?: number;
}

export function PaginatedLinks({
  links,
  initialLinks,
  total,
  route,
  itemsPerPage = 7,
}: PaginatedLinksProps) {
  const [currentPage, setCurrentPage] = useState(0);
  const [isPending, startTransition] = useTransition();
  const [currentLinks, setCurrentLinks] = useState<ProcessedLink[]>(
    initialLinks || links || []
  );

  // Determine if using server-side pagination (relational) or client-side (manual)
  const isServerSidePagination = route !== undefined && total !== undefined;
  const totalPages = isServerSidePagination
    ? Math.ceil(total / itemsPerPage)
    : Math.ceil((links?.length || 0) / itemsPerPage);
  const showPagination = isServerSidePagination
    ? total > itemsPerPage
    : (links?.length || 0) > itemsPerPage;

  // Client-side pagination for manual links
  const clientSideLinks = isServerSidePagination
    ? currentLinks
    : links?.slice(
        currentPage * itemsPerPage,
        (currentPage + 1) * itemsPerPage
      ) || [];

  const goToPrevious = () => {
    const newPage = currentPage > 0 ? currentPage - 1 : totalPages - 1;
    setCurrentPage(newPage);

    if (isServerSidePagination && route) {
      startTransition(async () => {
        const result = await fetchPaginatedPages(
          route,
          newPage + 1,
          itemsPerPage
        );
        setCurrentLinks(
          result.stories.map(
            (story: StoryblokStoryData): ProcessedLink => ({
              url: `/${story.full_slug}`,
              title:
                (story.content &&
                "title" in story.content &&
                typeof story.content.title === "string"
                  ? story.content.title
                  : null) ||
                story.name ||
                "Link",
              key: story.uuid,
            })
          )
        );
      });
    }
  };

  const goToNext = () => {
    const newPage = currentPage < totalPages - 1 ? currentPage + 1 : 0;
    setCurrentPage(newPage);

    if (isServerSidePagination && route) {
      startTransition(async () => {
        const result = await fetchPaginatedPages(
          route,
          newPage + 1,
          itemsPerPage
        );
        setCurrentLinks(
          result.stories.map(
            (story: StoryblokStoryData): ProcessedLink => ({
              url: `/${story.full_slug}`,
              title:
                (story.content &&
                "title" in story.content &&
                typeof story.content.title === "string"
                  ? story.content.title
                  : null) ||
                story.name ||
                "Link",
              key: story.uuid,
            })
          )
        );
      });
    }
  };

  const displayLinks = isServerSidePagination ? currentLinks : clientSideLinks;

  return (
    <div className="relative">
      <AnimatePresence mode="wait">
        <motion.div
          key={currentPage}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.3 }}
          className="flex flex-col divide-y-4 border-y-4 border-violet-900 divide-violet-900 mt-12"
        >
          {displayLinks.map((link) => (
            <div
              key={link.key}
              className="group relative py-8 hover:px-8 transition-all duration-300 overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-violet-900 via-violet-900 to-violet-950 scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300" />
              <Link
                href={link.url}
                className="relative text-4xl md:text-6xl font-display drop-shadow-2xl group-hover:text-teal-500 transition-all duration-300 flex items-center justify-between gap-12 z-10"
              >
                <span className="flex items-center gap-8">{link.title}</span>
                <ArrowRight className="size-10 shrink-0 md:size-24" />
              </Link>
            </div>
          ))}
        </motion.div>
      </AnimatePresence>

      {showPagination && (
        <div className="flex items-center justify-end gap-4 mt-8">
          <Button
            size="icon"
            onClick={goToPrevious}
            disabled={isPending}
            className="size-12"
            aria-label="Previous page"
          >
            <ChevronLeft className="size-8" />
          </Button>
          <Button
            size="icon"
            className="size-12"
            onClick={goToNext}
            disabled={isPending}
            aria-label="Next page"
          >
            <ChevronRight className="size-8" />
          </Button>
        </div>
      )}
    </div>
  );
}
